import type { AuditLogEntry } from "@/services/api/auditLog";
import { formatAuditDate, formatAuditTime, getAuditSource } from "../lib/auditFormat";
import styles from "../AuditLog.module.scss";

interface AuditCardProps {
  entry: AuditLogEntry;
}

const getInitials = (name: string | null) => {
  if (!name) return "A";
  const initials = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return initials || name.slice(0, 2).toUpperCase() || "A";
};

const getActorLabel = (entry: AuditLogEntry) => {
  if (entry.actorName) return entry.actorName;
  if (entry.actorId != null) return `Actor #${entry.actorId}`;
  return "Unknown";
};

const AuditCard = ({ entry }: AuditCardProps) => {
  const actorName = getActorLabel(entry);
  const initials = getInitials(entry.actorName);
  const source = getAuditSource(entry);
  const time = formatAuditTime(entry.createdAt);
  const date = formatAuditDate(entry.createdAt);

  return (
    <div className={styles.mobileCard}>
      <div className={styles.mobileCardHeader}>
        <div className={styles.mobileAvatar}>
          <span>{initials}</span>
        </div>

        <div className={styles.mobileCardInfo}>
          <h3 className={styles.mobileCardTitle}>{actorName}</h3>
          <p className={styles.mobileCardSubtitle}>{`${time} · ${date}`}</p>
        </div>

        <div className={styles.mobileCardActions}>
          <span
            className={
              source === "Member"
                ? styles.sourceMemberBadge
                : styles.sourceAdminBadge
            }
          >
            {source}
          </span>
        </div>
      </div>

      <div className={styles.mobileCardDivider} />

      <div className={styles.mobileCardGrid}>
        <div className={styles.mobileCardFieldFull}>
          <span className={styles.mobileFieldLabel}>Action</span>
          <span className={styles.mobileFieldValue}>{entry.action || "—"}</span>
        </div>

        <div className={styles.mobileCardField}>
          <span className={styles.mobileFieldLabel}>Module</span>
          <span className={styles.mobileFieldValue}>{entry.module || "—"}</span>
        </div>

        {entry.ipAddress ? (
          <div className={styles.mobileCardField}>
            <span className={styles.mobileFieldLabel}>IP Address</span>
            <span className={styles.mobileFieldValue}>{entry.ipAddress}</span>
          </div>
        ) : (
          <div className={styles.mobileCardField}>
            <span className={styles.mobileFieldLabel}>Source</span>
            <span className={styles.mobileFieldValue}>{source}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditCard;
